import { z } from "zod";

// Helper schema for color validation
const colorSchema = z
  .string()
  .refine(
    (val) =>
      /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(val) ||
      /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(val) ||
      /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*(0(\.\d+)?|1(\.0+)?)\s*\)$/.test(
        val,
      ) ||
      [
        "black",
        "white",
        "red",
        "green",
        "blue",
        "yellow",
        "purple",
        "pink",
        "orange",
        "gray",
      ].includes(val.toLowerCase()),
    { message: "Invalid color format. Use hex (#123ABC), RGB, or named color" },
  );

// Helper schema for size values
const sizeSchema = z.union([
  z.number(),
  z.string().refine((val) => /^\d+(%|px|em|rem|vh|vw|pt)$/.test(val), {
    message:
      "Size must be a number or a string with valid CSS units (px, em, rem, %, etc.)",
  }),
]);

// Shared Screen Format Schema
const sharedScreenFormatSchema = z.string().min(1, {
  message: "Shared screen format is required",
});
// General Settings Schema
const generalSettingsSchema = z.object({
  bingoGridTitle: z
    .string()
    .min(1, { message: "Bingo grid title is required" }),
  numbersRangeMin: z.number().int().min(1),
  numbersRangeMax: z.number().int().positive(),
  gridNumbersDisplay: z
    .string()
    .min(1, { message: "Grid numbers display is required" }),
  winningNumberOffset: z.number().int().positive().default(2),
  gridSelectionNumber: z.number().int().positive(),
  sharedScreenFormat: sharedScreenFormatSchema,
  textColor: colorSchema,
  alertColor: colorSchema,
});

// Front Office Messages Schema
const frontOfficeMessagesSchema = z.object({
  gameNotStarted: z
    .string()
    .min(1, { message: "Game not started message is required" }),
  gameEnded: z.string().min(1, { message: "Game ended message is required" }),
  gameAlreadyStarted: z
    .string()
    .min(1, { message: "Game already started message is required" }),
  gameWelcome: z
    .string()
    .min(1, { message: "Game welcome message is required" }),
  gameWelcomeButtonLabel: z
    .string()
    .min(1, { message: "Game welcome button label is required" }),
  gridChoice: z.string().min(1, { message: "Grid choice message is required" }),
  gridChoiceButtonLabel: z
    .string()
    .min(1, { message: "Grid choice button label is required" }),
  gameWaiting: z
    .string()
    .min(1, { message: "Game waiting message is required" }),
  drawExplainations: z
    .string()
    .min(1, { message: "Draw explainations message is required" }),
  notDrawnNumber: z
    .string()
    .min(1, { message: "Not drawn number message is required" }),
  drawnNumber: z
    .string()
    .min(1, { message: "Drawn number message is required" }),
  drawnNumbersLabel: z
    .string()
    .min(1, { message: "Drawn numbers label message is required" }),
  winnerPrivate: z
    .string()
    .min(1, { message: "Winner private message is required" }),
  looserPrivate: z
    .string()
    .min(1, { message: "Looser private message is required" }),
  welcomeReturnButtonLabel: z
    .string()
    .min(1, { message: "Welcome return button label is required" }),

  winnerTooLatePrivate: z
    .string()
    .min(1, { message: "Winner too late private message is required" }),
});

// Public Screen Messages Schema
const publicScreenMessagesSchema = z.object({
  welcome: z.string().min(1, { message: "Welcome message is required" }),
  drawInProgress: z
    .string()
    .min(1, { message: "Draw in progress message is required" }),
  winnerCheck: z
    .string()
    .min(1, { message: "Winner check message is required" }),
  endOfGame: z.string().refine((val) => val.includes("$$WINNER$$"), {
    message: "End of game message must include $$WINNER$$ placeholder",
  }),
});

// Images Schema
const imagesSchema = z.object({
  mobileBackground: z
    .string()
    .url({ message: "Mobile background must be a valid URL" }),
  screenBackgroundSize1: z
    .string()
    .url({ message: "Screen background size 1 must be a valid URL" }),
  screenBackgroundSize2: z
    .string()
    .url({ message: "Screen background size 2 must be a valid URL" }),
  screenLogo: z.string().url({ message: "Screen logo must be a valid URL" }),
});

// Private Screen Settings Schema
const privateScreenSettingsSchema = z.object({
  messagesSize: sizeSchema,
  messagesColor: colorSchema,
  buttonBackgroundColor: colorSchema,
  buttonsLabelColor: colorSchema,
  buttonsBorderColor: colorSchema,
  buttonsActiveColor: colorSchema,
  gridBgColor: colorSchema,
  gridNumberStandardColor: colorSchema,
  gridNumberSuccessBgColor: colorSchema,
  gridNumberFailureBgColor: colorSchema,
  gridNumberColor: colorSchema,
  popinBgColor: colorSchema,
  popinTextColor: colorSchema,
});

// Public Screen Settings Schema
const publicScreenSettingsSchema = z.object({
  messagesSize: sizeSchema,
  messagesColor: colorSchema,
  screenTopMargin: sizeSchema,
  screenLeftMargin: sizeSchema,
});

// Access Schema
const accessSchema = z.object({
  entity: z.string(),
  organization: z.string(),
  section: z.string(),
});

// BingoTemplate Schema
export const bingoTemplateSchema = z.object({
  access: accessSchema,
  creationTime: z
    .string()
    .datetime({ message: "Creation time must be a valid ISO datetime string" }),
  id: z.string().uuid({ message: "ID must be a valid UUID" }),
  name: z.string().min(1, { message: "Template name is required" }),
  generalSettings: generalSettingsSchema,
  frontOfficeMessages: frontOfficeMessagesSchema,
  publicScreenMessages: publicScreenMessagesSchema,
  images: imagesSchema,
  privateScreenSettings: privateScreenSettingsSchema,
  publicScreenSettings: publicScreenSettingsSchema,
});

// Export type
export type BingoTemplateValidated = z.infer<typeof bingoTemplateSchema>;
